import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_GATE_MODE } from "constants/proposals";

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

export const createStagesSlice = ({ name, hasDelete = false }) => {
  const stagesKey = name;
  const editableStagesKey = `editable${capitalize(name)}`;
  const setStagesAction = `set${capitalize(name)}`;
  const setEditableStagesAction = `setEditable${capitalize(name)}`;

  const initialState = {
    [stagesKey]: [],
    [editableStagesKey]: [],
    isEditing: false,
    isDirty: false,
    isLoading: false,
    isLocked: false,
    lockedBy: "",
    selectedIndex: -1,
    gateMode: DEFAULT_GATE_MODE,
    editableGateMode: DEFAULT_GATE_MODE,
  };

  const reducers = {
    [setStagesAction](state, { payload = [] }) {
      state[stagesKey] = payload;
    },
    [setEditableStagesAction](state, { payload = [] }) {
      state[editableStagesKey] = payload;
    },
    addStage(state, { payload }) {
      const { stage, index } = payload;
      state.isDirty = true;

      state[editableStagesKey] = [
        ...state[editableStagesKey].slice(0, index + 1),
        stage,
        ...state[editableStagesKey].slice(index + 1),
      ];
    },
    updateStage(state, { payload = {} }) {
      const { stage, index } = payload;
      state.isDirty = true;
      state[editableStagesKey] = state[editableStagesKey].map((item, i) => {
        return i === index ? { ...item, ...stage } : item;
      });
    },
    reorderStages(state, { payload }) {
      const { sourceIndex, destinationIndex } = payload;
      state.isDirty = true;
      const items = Array.from(state[editableStagesKey]);
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(destinationIndex, 0, removed);
      state[editableStagesKey] = items.map((item, index) => ({
        ...item,
        position: index + 1,
      }));
    },
    setIsEditing(state, { payload }) {
      state.isEditing = Boolean(payload);
    },
    setIsDirty(state, { payload }) {
      state.isDirty = Boolean(payload);
    },
    setIsLoading(state, { payload }) {
      state.isLoading = Boolean(payload);
    },
    setIsLocked(state, { payload }) {
      state.isLocked = Boolean(payload);
    },
    setLockedBy(state, { payload }) {
      state.lockedBy = payload;
    },
    setSelectedIndex(state, { payload = -1 }) {
      state.selectedIndex = payload;
    },
    setGateMode(state, { payload }) {
      state.gateMode = payload;
    },
    setEditableGateMode(state, { payload }) {
      state.editableGateMode = payload;
      state.isDirty = true;
    },
    setStageQuestions(state, { payload: { index, questions } }) {
      if (state[editableStagesKey][index]) {
        state[editableStagesKey][index].questions = questions;
        state.isDirty = true;
      }
    },
    addStageQuestion(state, { payload: { index, question } }) {
      const stage = state[editableStagesKey][index];
      if (stage) {
        stage.questions = [...(stage.questions || []), question];
        state.isDirty = true;
      }
    },
    removeStageQuestion(state, { payload: { stageIndex, questionIndex } }) {
      const stage = state[editableStagesKey][stageIndex];
      if (stage?.questions) {
        stage.questions = stage.questions.filter((_, i) => i !== questionIndex);
        state.isDirty = true;
      }
    },
    reorderStageQuestions(state, { payload: { stageIndex, sourceIndex, destinationIndex } }) {
      const stage = state[editableStagesKey][stageIndex];
      if (stage?.questions) {
        const next = [...stage.questions];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(destinationIndex, 0, moved);
        stage.questions = next;
        state.isDirty = true;
      }
    },
  };

  if (hasDelete) {
    reducers.removeStage = (state, action) => {
      const index = action.payload;
      state.isDirty = true;
      state[editableStagesKey] = state[editableStagesKey].filter(
        (_, i) => i !== index
      );
    };
  }

  return createSlice({ name, initialState, reducers });
};
